import { useState } from 'react';
import { Link } from 'react-router-dom';
import { assetUrl } from '../api/client';
import { useAllFindings, useMaps } from '../api/queries';
import type { ResearchMap } from '../api/types';
import { findingPath } from '../components/FindingCard';
import { MapTree } from '../components/MapTree';
import { EmptyState, QueryState } from '../components/States';
import { formatDate, plural } from '../lib/format';
import { mapView } from '../lib/maps';

const ZOOMS = [0.25, 0.5, 1] as const;

function OriginalImage({ map }: { map: ResearchMap }) {
  const [zoom, setZoom] = useState<(typeof ZOOMS)[number]>(0.25);
  const [failed, setFailed] = useState(false);
  if (map.assetId === undefined) {
    return (
      <p className="muted">
        The original image is served only through the protected asset route. It has not been linked to an asset record
        for your account yet, so the transcription below is shown on its own.
      </p>
    );
  }
  if (failed) {
    return (
      <p className="muted" role="status">
        The original image could not be loaded. It may be restricted for your role, or its archive file is unavailable.
      </p>
    );
  }
  return (
    <details className="original">
      <summary>View the original image</summary>
      <div className="original__tools" role="group" aria-label="Image zoom">
        {ZOOMS.map((value) => (
          <button
            key={value}
            type="button"
            className={zoom === value ? 'button button--secondary button--pressed' : 'button button--secondary'}
            aria-pressed={zoom === value}
            onClick={() => setZoom(value)}
          >
            {value * 100}%
          </button>
        ))}
      </div>
      <div className="original__frame" tabIndex={0} role="region" aria-label={`${map.title}, original image; scroll to pan`}>
        <img
          src={assetUrl(map.assetId)}
          alt={`Original ${map.title}. Its readable labels are transcribed in the tree below.`}
          style={{ width: `${String(zoom * 100)}%`, maxWidth: 'none' }}
          onError={() => setFailed(true)}
        />
      </div>
    </details>
  );
}

function MapSection({ map, findingTitles }: { map: ResearchMap; findingTitles: Map<string, string> }) {
  const view = mapView(map);
  return (
    <section className="map-section" aria-labelledby={`map-${map.id}`}>
      <header className="map-section__head">
        <p className="eyebrow">Original map · supplied by the owner</p>
        <h2 id={`map-${map.id}`} className="chapter__title">
          {map.title}
        </h2>
        {map.description !== undefined && <p className="lede">{map.description}</p>}
        <dl className="finding__facts">
          <div>
            <dt>Created</dt>
            <dd>{map.sourceCreationDate === undefined || map.sourceCreationDate === null ? 'No confirmed date' : formatDate(map.sourceCreationDate)}</dd>
          </div>
          <div>
            <dt>Root</dt>
            <dd>{view.rootVisible === false ? 'Cropped in the supplied image' : view.rootVisible === true ? 'Visible' : 'Not recorded'}</dd>
          </div>
          {map.sourceHash !== undefined && (
            <div>
              <dt>Image fingerprint</dt>
              <dd className="mono">{map.sourceHash.slice(0, 12)}…</dd>
            </div>
          )}
        </dl>
      </header>
      <div className="map-section__body">
        <div className="map-section__tree">
          <OriginalImage map={map} />
          {view.nodes.length === 0 ? (
            <p className="notice notice--flag" role="note">
              {view.unreadable > 0
                ? `The saved structure for this map could not be read (${plural(view.unreadable, 'unrecognised entry', 'unrecognised entries')}). Nothing is reconstructed in its place.`
                : 'No transcribed structure is available for this map.'}
            </p>
          ) : (
            <>
              {view.unreadable > 0 && (
                <p className="small muted" role="note">
                  {plural(view.unreadable, 'entry', 'entries')} in the saved structure could not be read and {view.unreadable === 1 ? 'is' : 'are'} not shown.
                </p>
              )}
              <MapTree nodes={view.nodes} label={`${map.title}, transcribed hierarchy`} />
            </>
          )}
        </div>
        <aside className="map-section__notes" aria-label={`About the ${map.title} transcription`}>
          {map.transcription !== undefined && <p>{map.transcription}</p>}
          {view.groupingFrameLabels.length > 0 && (
            <>
              <p className="minor-title">Grouping frames on the board</p>
              <ul className="point-list">
                {view.groupingFrameLabels.map((label) => (
                  <li key={label}>{label}</li>
                ))}
              </ul>
            </>
          )}
          <p className="minor-title">Limits</p>
          <ul className="point-list point-list--limits">
            {(map.limits ?? []).map((limit) => (
              <li key={limit}>{limit}</li>
            ))}
            {view.hiddenContent !== undefined && <li>{view.hiddenContent}</li>}
          </ul>
          {(map.findingIds ?? []).length > 0 && (
            <>
              <p className="minor-title">Findings that use this map</p>
              <ul className="point-list">
                {(map.findingIds ?? []).map((id) => (
                  <li key={id}>
                    <Link to={findingPath(id)}>{findingTitles.get(id) ?? 'A finding you may not have access to'}</Link>
                  </li>
                ))}
              </ul>
            </>
          )}
        </aside>
      </div>
    </section>
  );
}

export function MapsPage() {
  const maps = useMaps();
  const findings = useAllFindings({});
  const findingTitles = new Map((findings.data?.items ?? []).map((finding) => [finding.id, finding.title]));
  return (
    <main id="main" tabIndex={-1} className="page page--wide">
      <header className="page__header">
        <p className="eyebrow">Original maps</p>
        <h1 className="page__title">The maps the community drew for itself</h1>
        <p className="lede">
          Two boards set out the professional playground and the wider strategy: who takes part, how people grow, and
          where the community hoped to go. They record intentions, not practice — the report compares the two. Every
          readable label is transcribed; nothing hidden, collapsed or cropped is guessed.
        </p>
      </header>
      <QueryState query={maps} label="maps">
        {(page) =>
          page.items.length === 0 ? (
            <EmptyState title="No maps are visible to your account">
              <p>They may not be imported yet, or they may be restricted.</p>
            </EmptyState>
          ) : (
            <div className="maps">
              {page.items.map((map) => (
                <MapSection key={map.id} map={map} findingTitles={findingTitles} />
              ))}
            </div>
          )
        }
      </QueryState>
    </main>
  );
}
